import { TicketBuyerLimits } from "@ac-expo/api-types";
import {
  BuyableEntryType,
  BuyableEntryTypeWithKey,
  EntryType,
  UseLocalCartReturns,
} from "@ac-expo/hooks";
import {
  centsToPrice,
  getGroupPurchaseMinimumMessage,
  getTicketPurchaseAllowance,
} from "@ac-expo/utils";
import { cn } from "@ui/utils";
import { MinusCircleIcon, PlusCircleIcon } from "../../icons";

type SingleItemTicketingProps<T extends EntryType> = {
  entry: BuyableEntryTypeWithKey<T>;
  localCart: UseLocalCartReturns;
  // The visitor's ticket budget as the server sees it; badges carry no per-person limit.
  buyer?: TicketBuyerLimits;
};

const isTicketEntryType = (
  entryType: BuyableEntryType<EntryType>,
): entryType is BuyableEntryType<"ticket"> => "buyingLimitForUserType" in entryType;

export function SingleItemTicketing<T extends EntryType>({
  entry,
  localCart,
  buyer,
}: SingleItemTicketingProps<T>) {
  const { entryType, itemKey } = entry;
  const {
    incrementItem,
    decrementItem,
    getLocalQuantity,
    getTotalQuantity,
    getItemFinalPricing,
  } = localCart;

  const quantity = getLocalQuantity(itemKey);

  // The « Group purchase » minimum is checked against everything the basket already holds for
  // this entry, exactly as the basket validation counts it — not against this session's picks.
  const groupPurchaseMinimumMessage = getGroupPurchaseMinimumMessage(
    entryType,
    getTotalQuantity(itemKey)
  );
  const pricingData = entryType.pricing;
  const basePrice = centsToPrice(pricingData?.price?.priceTTC ?? 0);
  const finalPricing = getItemFinalPricing(itemKey);
  const unitPrice = finalPricing?.finalUnitPrice
    ? centsToPrice(finalPricing.finalUnitPrice)
    : basePrice;
  const hasDiscount = unitPrice < basePrice;

  const isSoldOut =
    entryType.quantityLeft === 0 && !entryType.isQuantityInfinite;

  const formatPrice = (price: number) => {
    if (price === 0) {
      return "Gratuit";
    }
    return `${price.toFixed(2)}€`;
  };

  const { limit: userLimit, remaining: remainingAllowance } = isTicketEntryType(
    entryType,
  )
    ? getTicketPurchaseAllowance(entryType, buyer)
    : { limit: null, remaining: null };

  const isUserLimitReached =
    remainingAllowance !== null && quantity >= remainingAllowance;
  const increaseDisabled =
    isSoldOut ||
    (entryType.quantityLeft !== null && quantity >= entryType.quantityLeft) ||
    isUserLimitReached;

  return (
    <div className="grid grid-cols-[1fr_max-content] items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-col gap-2">
        <p className="text-lg font-bold leading-none">
          {hasDiscount && (
            <span className="mr-2">{formatPrice(unitPrice)}</span>
          )}
          <span
            className={cn({
              "text-sm text-gray-500 line-through": hasDiscount,
            })}
          >
            {formatPrice(basePrice)}
          </span>
          {hasDiscount && (
            <span className="ml-2 text-[10px] font-normal text-gray-500">
              Prix du billet suivant après application de la dégressivité
            </span>
          )}
        </p>
        <p className="text-sm font-medium">
          {entryType.name}{" "}
          <span className="font-light text-gray-600">
            {isSoldOut && "(épuisé)"}
          </span>
        </p>
        {isUserLimitReached && (
          <p className="text-xs text-red-500">
            {"Limite d'achat atteinte ("}{userLimit}{" max)"}
          </p>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 pl-4">
        <button
          aria-label={`Retirer un ${entryType.name}`}
          onClick={() => decrementItem(itemKey)}
          disabled={quantity === 0}
        >
          <MinusCircleIcon
            width={26}
            height={26}
            fill={quantity === 0 ? "#98A2B3" : "#287BB9"}
          />
        </button>
        <span className="w-8 text-center text-base font-bold">{quantity}</span>
        <button
          aria-label={`Ajouter un ${entryType.name}`}
          onClick={() => incrementItem(itemKey)}
          disabled={increaseDisabled}
        >
          <PlusCircleIcon
            width={26}
            height={26}
            fill={increaseDisabled ? "#98A2B3" : "#287BB9"}
          />
        </button>
      </div>
      {groupPurchaseMinimumMessage && (
        <span className="col-span-full text-end text-xs text-red-500">
          {groupPurchaseMinimumMessage}
        </span>
      )}
    </div>
  );
}
